import { AuthContext } from "@/auth/authcontext";
import {
  PostHogProvider as PostHogProviderBase,
  usePostHog,
} from "posthog-js/react";
import type { PropsWithChildren } from "react";
import { useContext, useEffect, useRef } from "react";
import { useLocation } from "react-router";

const POSTHOG_CONFIG = {
  api_key: import.meta.env.VITE_POSTHOG_KEY,
  options: {
    api_host: "https://us.i.posthog.com",
    person_profiles: "always" as const,
    capture_pageview: false,
    session_recording: {
      maskAllInputs: false,
      maskInputOptions: {
        password: true,
        email: true,
        tel: true,
      },
    },
  },
} as const;

export function PostHogProvider({ children }: PropsWithChildren) {
  return (
    <PostHogProviderBase
      apiKey={POSTHOG_CONFIG.api_key}
      options={POSTHOG_CONFIG.options}
    >
      <PostHogTracker>{children}</PostHogTracker>
    </PostHogProviderBase>
  );
}

function PostHogTracker({ children }: PropsWithChildren) {
  const posthog = usePostHog();
  const location = useLocation();
  const lastPageView = useRef<string>();
  const lastUserId = useRef<string>();
  const { user } = useContext(AuthContext);

  // handle pageviews
  useEffect(() => {
    if (!posthog) return;
    const currentUrl = window.location.href;
    if (lastPageView.current === currentUrl) return;
    lastPageView.current = currentUrl;
    posthog.capture("$pageview", {
      $current_url: currentUrl,
    });
  }, [location, posthog]);

  // Handle user identification and feature flag reloading
  useEffect(() => {
    if (!posthog || !user) return;
    if (lastUserId.current === String(user.id)) return;

    lastUserId.current = String(user.id);
    posthog.identify(String(user.id), {
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      get_display_name: user.get_display_name,
    });
    posthog.reloadFeatureFlags();
  }, [posthog, user]);

  return children;
}
